import { useState } from "react"
import { DayPicker } from "react-day-picker"
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react"
import { Popover, PopoverTrigger, PopoverContent } from "./Popover"
import { cn } from "../../lib/utils"

interface DatePickerProps {
  value?: string // ISO yyyy-mm-dd
  onChange: (value: string) => void
  placeholder?: string
  id?: string
  disabled?: boolean
  className?: string
  /** Earliest pickable date (ISO). */
  fromDate?: string
  /** Latest pickable date (ISO). */
  toDate?: string
}

// ISO yyyy-mm-dd <-> local Date, kept timezone-safe by anchoring to midnight.
function isoToDate(value?: string): Date | undefined {
  if (!value) return undefined
  const d = new Date(value + "T00:00:00")
  return isNaN(d.getTime()) ? undefined : d
}

function dateToIso(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

function formatDisplay(date: Date): string {
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

const DatePicker = ({ value, onChange, placeholder = "Pick a date", id, disabled, className, fromDate, toDate }: DatePickerProps) => {
  const [open, setOpen] = useState(false)
  const selected = isoToDate(value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        id={id}
        type="button"
        disabled={disabled}
        className={cn(
          "flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs transition-colors",
          "focus-visible:outline-none focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50",
          !selected && "text-muted-foreground",
          className
        )}
      >
        <span className="truncate">{selected ? formatDisplay(selected) : placeholder}</span>
        <CalendarIcon className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
      </PopoverTrigger>
      <PopoverContent className="w-auto" align="start">
        <DayPicker
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={(date) => {
            if (date) {
              onChange(dateToIso(date))
              setOpen(false)
            }
          }}
          startMonth={isoToDate(fromDate)}
          endMonth={isoToDate(toDate)}
          disabled={[
            ...(fromDate ? [{ before: isoToDate(fromDate)! }] : []),
            ...(toDate ? [{ after: isoToDate(toDate)! }] : []),
          ]}
          showOutsideDays
          components={{
            Chevron: ({ orientation }) =>
              orientation === "left" ? (
                <ChevronLeft className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              ),
          }}
          classNames={{
            months: "relative flex flex-col",
            month: "space-y-3",
            month_caption: "flex items-center justify-center h-9 relative",
            caption_label: "text-sm font-medium pointer-events-none",
            nav: "flex items-center justify-between absolute inset-x-0 top-0 h-9 z-10",
            button_previous:
              "inline-flex h-9 w-9 items-center justify-center text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40 cursor-pointer",
            button_next:
              "inline-flex h-9 w-9 items-center justify-center text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40 cursor-pointer",
            month_grid: "w-full border-collapse",
            weekdays: "flex",
            weekday: "w-9 text-xs font-medium text-muted-foreground",
            week: "flex w-full mt-1",
            day: "h-9 w-9 p-0 text-center text-sm",
            day_button:
              "inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors hover:bg-accent hover:text-accent-foreground aria-selected:bg-primary aria-selected:text-primary-foreground aria-selected:hover:bg-primary",
            selected: "[&>button]:bg-primary [&>button]:text-primary-foreground",
            today: "[&:not(.rdp-selected)>button]:border [&:not(.rdp-selected)>button]:border-border",
            outside: "text-muted-foreground opacity-50",
            disabled: "opacity-40",
            hidden: "invisible",
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

export { DatePicker }
